import { logger } from '@user-portal/logger';
import { FirehoseClient, PutRecordCommand } from '@aws-sdk/client-firehose';
import { fromCognitoIdentityPool, fromCognitoIdentity } from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { EventWriter } from 'experimentation-client-js';

/**
 * Configuration for creating a Firehose event writer
 */
export interface FirehoseEventWriterConfig {
    /**
     * AWS region (default: 'us-east-1')
     */
    region?: string;

    /**
     * Cognito Identity Pool ID for authentication
     */
    identityPoolId: string;

    /**
     * Optional: Cognito Identity ID for direct identity authentication
     */
    identityId?: string;

    /**
     * Firehose delivery stream name
     */
    streamName: string;

    /**
     * Event source identifier to be added to all events
     */
    eventSourceName: string;
}

let sharedFirehoseClient: FirehoseClient | undefined;

/**
 * Creates an event writer that sends events to AWS Kinesis Firehose
 *
 * @param config - Configuration for the Firehose writer
 * @returns EventWriter instance
 */
export const createFirehoseEventWriter = (config: FirehoseEventWriterConfig): EventWriter => {
    const { region = 'us-east-1', identityPoolId, identityId, streamName, eventSourceName } = config;

    if (!sharedFirehoseClient) {
        const client = new CognitoIdentityClient({ region });

        const credentials = identityId
            ? fromCognitoIdentity({
                  identityId,
                  client,
              })
            : fromCognitoIdentityPool({
                  identityPoolId,
                  client,
              });

        sharedFirehoseClient = new FirehoseClient({
            region,
            credentials,
        });
    }

    const firehose = sharedFirehoseClient;

    const handleSendRecord = async (event: unknown): Promise<boolean> => {
        if (!firehose) {
            logger.error('Firehose client not initialized');
            return false;
        }

        try {
            const payload =
                typeof event === 'object'
                    ? { ...event, __source: eventSourceName }
                    : { value: event, __source: eventSourceName };

            const data = new TextEncoder().encode(JSON.stringify(payload));

            const command = new PutRecordCommand({
                DeliveryStreamName: streamName,
                Record: { Data: data },
            });

            await firehose.send(command);

            logger.info('Event sent to Firehose', { streamName, eventSourceName });

            return true;
        } catch (error: unknown) {
            logger.error('Failed to send event to Firehose', {
                error,
                streamName,
                eventSourceName,
            });

            return false;
        }
    };

    return {
        streamName,
        eventSourceName,
        sendRecord: handleSendRecord,
    };
};
